import {Component, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ReviewType} from '../../../shared/models/submission.model';
import {ALL_REVIEW_TYPES, reviewTypeDescription, reviewTypeLabel} from '../../../shared/services/submission-display';

/** Data passed in when opening the dialog: the current selection + a callback applied on every change. */
export interface RequiredReviewsDialogData {
  requiredReviews: ReviewType[];
  /** Called with the new selection whenever a review type is toggled (immediate-apply). */
  onChange: (reviews: ReviewType[]) => void;
}

/**
 * Form-builder dialog for choosing which human reviews a form requires (e.g. payroll). Opened via MatDialog so it
 * renders in the CDK overlay at the document root — above the builder's split-view panels and any stacking
 * context (a plain in-canvas `fixed` popup was trapped under the preview panel's `relative z-0` column). The
 * content matches the app's general popup look; MatDialog provides the surface + dim backdrop.
 */
@Component({
  selector: 'forms-required-reviews-popup',
  standalone: true,
  imports: [],
  template: `
    <div class="flex flex-col" role="dialog" aria-labelledby="required-reviews-title">
      <div class="border-b border-gray-100 pb-3">
        <h2 id="required-reviews-title" class="text-xl font-bold tracking-tight text-gray-900">Erforderliche Prüfungen</h2>
        <p class="mt-1 text-sm text-gray-400">Wählen Sie, welche Prüfungen eine Einreichung dieses Formulars benötigt.</p>
      </div>

      <ul class="flex flex-col gap-2 py-4">
        @for (type of allTypes; track type) {
          <li>
            <label class="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-100 p-3 transition-colors hover:bg-gray-50">
              <input type="checkbox" [checked]="isSelected(type)" (change)="toggle(type)"
                     class="mt-0.5 h-4 w-4 rounded border-gray-300 text-theme-primary-600 focus:ring-theme-primary-500"/>
              <span class="flex flex-col">
                <span class="text-sm font-semibold text-gray-900">{{ label(type) }}</span>
                <span class="text-xs text-gray-400">{{ description(type) }}</span>
              </span>
            </label>
          }
        </ul>

      <div class="flex justify-end border-t border-gray-100 pt-3">
        <button type="button" (click)="done()"
                class="inline-flex justify-center rounded-md bg-theme-primary-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-theme-primary-900 focus:outline-none focus:ring-2 focus:ring-theme-primary-500 focus:ring-offset-2">
          Fertig
        </button>
      </div>
    </div>
  `
})
export class RequiredReviewsPopupComponent {
  protected readonly allTypes = ALL_REVIEW_TYPES;
  protected readonly label = reviewTypeLabel;
  protected readonly description = reviewTypeDescription;

  protected selected: ReviewType[];

  constructor(
    private ref: MatDialogRef<RequiredReviewsPopupComponent>,
    @Inject(MAT_DIALOG_DATA) private data: RequiredReviewsDialogData
  ) {
    this.selected = [...(data?.requiredReviews ?? [])];
  }

  protected isSelected(type: ReviewType): boolean {
    return this.selected.includes(type);
  }

  protected toggle(type: ReviewType): void {
    this.selected = this.isSelected(type)
      ? this.selected.filter(t => t !== type)
      : [...this.selected, type];
    this.data.onChange(this.selected);
  }

  protected done(): void {
    this.ref.close();
  }
}
